import type { Rotation } from './projection.js';

/** Frames per full turn for every heading-bearing sprite in the production atlas. */
export const HEADING_COUNT = 64;

/** Rail sprites are symmetric under a half turn (C(t) = t mod pi); everything else is directed. */
export type HeadingKind = 'rail' | 'directed';

const TAU = Math.PI * 2;
const QUARTER_TURNS = 4;
const RAIL_SPRITES: ReadonlySet<string> = new Set(['track', 'track/deck']);

export const headingKindForSprite = (id: string): HeadingKind => (RAIL_SPRITES.has(id) ? 'rail' : 'directed');

const positiveModulo = (value: number, modulus: number): number => ((value % modulus) + modulus) % modulus;

function assertFrameCount(count: number): void {
  if (!Number.isInteger(count) || count < 1 || (count !== 1 && count % QUARTER_TURNS !== 0)) {
    throw new Error(`invalid heading frame count ${count}: expected 1 or a positive multiple of ${QUARTER_TURNS}`);
  }
}

const canonicalHeading = (heading: number, kind: HeadingKind): number =>
  kind === 'rail' ? positiveModulo(heading, Math.PI) : heading;

/** frame = floor(C(t)/Δ + 1/2 + q·count/4) mod count, with Δ = 2π/count. */
export function frameForHeading(heading: number, cameraRotation: Rotation, kind: HeadingKind, count: number = HEADING_COUNT): number {
  assertFrameCount(count);
  if (count === 1) return 0;
  const step = TAU / count;
  const index = Math.floor(canonicalHeading(heading, kind) / step + 0.5 + cameraRotation * count / QUARTER_TURNS);
  return positiveModulo(index, count);
}

/** Centre heading (camera quarter-turn 0) that the atlas renders into `frame`. */
export function headingForFrame(frame: number, count: number = HEADING_COUNT): number {
  assertFrameCount(count);
  if (!Number.isInteger(frame) || frame < 0 || frame >= count) throw new Error(`invalid heading frame ${frame} for ${count} frames`);
  return frame * TAU / count;
}
